import { z } from 'zod';

/**
 * The operator-set trading rules — leverage, option-sell margin, and
 * brokerage. Each is a global default the admin can override per user, so the
 * shapes here carry BOTH: `TradingDefaults` is the global config, and a user's
 * effective config is the defaults with any per-user override applied.
 *
 * These numbers are the platform's core product decisions:
 *  - leverage is the USP (up to 500x),
 *  - the option-sell margin rate is what makes writing an option cost real
 *    capital (≈ SPAN),
 *  - brokerage is the operator's own fee, replacing statutory charges.
 */

/** Hard ceiling on leverage — the product promises "up to 500x". */
export const MAX_LEVERAGE = 500;

export type BrokerageMode = 'per_order' | 'per_lot';
export const BrokerageModeSchema = z.enum(['per_order', 'per_lot']);

/**
 * Per-user overrides. Every field is optional: absent means "use the global
 * default". Bounds are enforced here so an admin typo can't set 900x or a
 * negative fee.
 */
export const UserTradingOverrideSchema = z.object({
  /** Whole-number multiplier, 1..500. */
  leverage: z.number().int().min(1).max(MAX_LEVERAGE).optional(),
  /** Option-sell margin as a fraction of underlying notional (0.065 = 6.5%). */
  sellMarginRate: z.number().min(0.001).max(1).optional(),
  brokerageMode: BrokerageModeSchema.optional(),
  /** Rupees per order (per_order) or per lot (per_lot). */
  brokerageValue: z.number().min(0).max(100_000).optional(),
});
export type UserTradingOverride = z.infer<typeof UserTradingOverrideSchema>;

/** The global defaults, every field required — the fallback for all users. */
export const TradingDefaultsSchema = z.object({
  leverage: z.number().int().min(1).max(MAX_LEVERAGE),
  sellMarginRate: z.number().min(0.001).max(1),
  brokerageMode: BrokerageModeSchema,
  brokerageValue: z.number().min(0).max(100_000),
});
export type TradingDefaults = z.infer<typeof TradingDefaultsSchema>;

/**
 * The product's shipped defaults. 300x leverage (headline is 500x, but a
 * saner default per user), ≈SPAN option-sell margin, flat ₹20/order brokerage.
 */
export const DEFAULT_TRADING: TradingDefaults = {
  leverage: 300,
  sellMarginRate: 0.065,
  brokerageMode: 'per_order',
  brokerageValue: 20,
};

/** A user's effective config — defaults with their overrides applied. */
export type EffectiveTrading = TradingDefaults;

export function resolveTrading(
  defaults: TradingDefaults,
  override: UserTradingOverride | null | undefined,
): EffectiveTrading {
  return {
    leverage: override?.leverage ?? defaults.leverage,
    sellMarginRate: override?.sellMarginRate ?? defaults.sellMarginRate,
    brokerageMode: override?.brokerageMode ?? defaults.brokerageMode,
    brokerageValue: override?.brokerageValue ?? defaults.brokerageValue,
  };
}

// ── operator risk controls ──────────────────────────────────────────────────

/**
 * The operator's platform-wide risk switchboard (app_config key 'risk').
 * Everything here is enforced server-side at the single points money moves:
 * the order gate, the deposit route and the withdrawal route. The admin panel
 * is just an editor for this object.
 *
 *  - tradingMode: 'open' trades normally; 'close_only' accepts only orders
 *    that REDUCE an open position (the operator is winding risk down without
 *    trapping anyone); 'halted' rejects every order.
 *  - blockedSymbols: per-symbol halt — an erratic feed on one contract must
 *    not require halting the whole book. Matched case-insensitively.
 *  - maxOrderNotional: hard rupee cap per order (price × qty), on top of the
 *    margin check. null = no cap.
 *  - maxOrdersPerUserPerDay: circuit breaker for runaway scripts. null = none.
 *  - withdrawalsRequireKyc: gate payouts on an operator-verified identity.
 *  - min/maxWithdrawal…: payout size rails; maxWithdrawalPerDay sums the
 *    day's requests (IST), null = no daily cap.
 */
export const RiskControlsSchema = z.object({
  tradingMode: z.enum(['open', 'close_only', 'halted']).default('open'),
  /** Shown verbatim in the rejection when trading is not 'open'. */
  haltMessage: z.string().trim().max(200).default(''),
  blockedSymbols: z.array(z.string().trim().min(1).max(60)).max(500).default([]),
  maxOrderNotional: z.number().positive().nullable().default(null),
  maxOrdersPerUserPerDay: z.number().int().min(1).nullable().default(null),
  withdrawalsRequireKyc: z.boolean().default(false),
  minWithdrawal: z.number().int().min(1).max(10_000_000).default(100),
  maxWithdrawalPerDay: z.number().positive().nullable().default(null),
  depositsDisabled: z.boolean().default(false),
  withdrawalsDisabled: z.boolean().default(false),
});
export type RiskControls = z.infer<typeof RiskControlsSchema>;

export const DEFAULT_RISK: RiskControls = RiskControlsSchema.parse({});

/**
 * Does this order violate the operator's risk controls? Pure — the engine
 * calls it with the order's resolved notional and whether it only reduces an
 * open position. Returns the human-readable rejection, or null when clean.
 */
export function riskViolation(
  risk: RiskControls,
  order: { symbol: string; notional: number; reducing: boolean },
): string | null {
  const note = risk.haltMessage ? ` — ${risk.haltMessage}` : '';
  if (risk.tradingMode === 'halted') return `trading is paused by the operator${note}`;
  if (
    risk.blockedSymbols.some((s) => s.toUpperCase() === order.symbol.toUpperCase()) &&
    // A blocked symbol still lets holders OUT — blocking exits turns a halt
    // into a trap, and the operator's exposure only shrinks on a close.
    !order.reducing
  ) {
    return `new positions in ${order.symbol} are paused by the operator${note}`;
  }
  if (risk.tradingMode === 'close_only' && !order.reducing) {
    return `only closing orders are accepted right now${note}`;
  }
  if (risk.maxOrderNotional != null && order.notional > risk.maxOrderNotional && !order.reducing) {
    return `order value ₹${Math.round(order.notional).toLocaleString('en-IN')} exceeds the per-order cap of ₹${Math.round(risk.maxOrderNotional).toLocaleString('en-IN')}`;
  }
  return null;
}

/**
 * How an instrument is margined. The three cases behave completely
 * differently, and this is the single place the distinction is decided so the
 * order ticket, the margin check and the fill all agree.
 *
 *  - directional: equity and futures. Margin = value / leverage. This is where
 *    the leverage USP lives.
 *  - option_buy: pay the full premium; leverage never applies (you cannot risk
 *    less than what you paid), and no liquidation is possible.
 *  - option_sell: writing an option. Margin = underlying notional × the sell
 *    rate (≈ SPAN); leverage does not apply, matching Kite/Groww.
 */
export type MarginModel = 'directional' | 'option_buy' | 'option_sell';

export function marginModelOf(kind: string | undefined, side: 'BUY' | 'SELL'): MarginModel {
  if (kind === 'option') return side === 'BUY' ? 'option_buy' : 'option_sell';
  return 'directional';
}

/**
 * Margin required to OPEN `qty` of exposure, in rupees. Pure and total — the
 * one function the ticket estimate, the order gate and the position ledger all
 * call, so a quoted margin and the debited margin can never diverge.
 *
 * `refPrice` is the fill/limit price (the premium, for options). `strike` is
 * the option's strike, used as the underlying-notional proxy when writing —
 * it is always present in contract metadata and ≈ spot near the money. (A live
 * underlying spot would be marginally more accurate for far strikes; the
 * configurable rate absorbs the difference.)
 */
export function requiredMargin(args: {
  model: MarginModel;
  qty: number;
  refPrice: number;
  strike: number;
  cfg: EffectiveTrading;
}): number {
  const { model, qty, refPrice, strike, cfg } = args;
  const q = Math.abs(qty);
  if (q <= 0 || !Number.isFinite(refPrice)) return 0;
  switch (model) {
    case 'directional':
      return (q * refPrice) / cfg.leverage;
    case 'option_buy':
      // Full premium — this is both the cost and the max loss.
      return q * refPrice;
    case 'option_sell': {
      // Underlying notional × rate. Strike stands in for spot.
      const notional = q * (strike > 0 ? strike : refPrice);
      return notional * cfg.sellMarginRate;
    }
  }
}

/**
 * The operator's brokerage for one executed leg, in rupees. Replaces the
 * statutory charge model entirely (nothing here hits a real exchange, so there
 * is no STT/GST to collect — only the operator's own fee). Charged on every
 * leg, buy and sell alike.
 *
 * per_order: a flat fee regardless of size.
 * per_lot:   fee × number of lots (equity lot size is 1, so it is fee × qty).
 */
export function brokerageFor(args: {
  cfg: EffectiveTrading;
  qty: number;
  lotSize: number;
}): number {
  const { cfg, qty, lotSize } = args;
  const q = Math.abs(qty);
  if (q <= 0) return 0;
  if (cfg.brokerageMode === 'per_order') return cfg.brokerageValue;
  const lots = Math.max(1, Math.ceil(q / Math.max(1, lotSize)));
  return cfg.brokerageValue * lots;
}

/**
 * Fraction of a position's posted margin that its unrealized loss may reach
 * before the risk engine force-closes it. 0.9 leaves a 10% buffer to absorb
 * the slippage between the breach and the fill, so a normally-moving position
 * is flattened while it still has margin left — that buffer is what keeps the
 * account from going negative.
 */
export const LIQUIDATION_MAINTENANCE = 0.9;

/** Unrealized P&L of a position at a mark (sign follows qty: shorts profit as price falls). */
export function unrealizedPnl(qty: number, avgPrice: number, ltp: number): number {
  return (ltp - avgPrice) * qty;
}

/**
 * Whether a position must be liquidated — its unrealized loss has reached
 * `maintenance` of the margin backing it.
 *
 * Long options are exempt: their entire risk is the premium already paid, so
 * they carry no negative-balance exposure and are never margin-called (you
 * simply lose the premium, as with a real broker). Directional positions and
 * WRITTEN options are swept.
 */
export function shouldLiquidate(args: {
  kind: string;
  qty: number;
  avgPrice: number;
  ltp: number;
  margin: number;
  maintenance?: number;
}): boolean {
  const { kind, qty, avgPrice, ltp, margin } = args;
  const maintenance = args.maintenance ?? LIQUIDATION_MAINTENANCE;
  if (margin <= 0 || qty === 0) return false;
  if (kind === 'option' && qty > 0) return false;
  return unrealizedPnl(qty, avgPrice, ltp) <= -maintenance * margin;
}

/**
 * The bankruptcy price of a position — the mark at which its loss exactly
 * equals the margin backing it. Liquidation never books a loss worse than
 * this: if the market gaps past it, the trader forfeits exactly their margin
 * and the platform absorbs the excess. This is negative-balance protection,
 * and it is how real leveraged venues bound a liquidation.
 *
 *   long : loss = (close − avg)·qty = −margin  ⇒  close = avg − margin/qty
 *   short: loss = (close − avg)·qty = −margin  ⇒  close = avg + margin/|qty|
 */
export function bankruptcyPrice(qty: number, avgPrice: number, margin: number): number {
  const q = Math.abs(qty);
  if (q <= 0) return avgPrice;
  return qty > 0 ? avgPrice - margin / q : avgPrice + margin / q;
}

/**
 * Clamp a liquidation fill price so the booked loss can never exceed the
 * posted margin. For a long the fill is floored at the bankruptcy price (a
 * lower mark would lose more than the margin); for a short it is capped there.
 * A mark on the safe side of bankruptcy passes through unchanged.
 */
export function clampLiquidationPrice(qty: number, avgPrice: number, margin: number, mark: number): number {
  const bp = bankruptcyPrice(qty, avgPrice, margin);
  return qty > 0 ? Math.max(mark, bp) : Math.min(mark, bp);
}
